/**
 * First-party product analytics for the staff app — no third party.
 * Batches view/click/ping/leave/return events into public.app_events.
 * Skipped entirely in dev and for unauthenticated visitors.
 */
import { supabase } from "@/integrations/supabase/client";
import { redactRoute } from "@/lib/redactRoute";

type Ev = "view" | "click" | "ping" | "leave" | "return";
type Row = { sid: string; ev: Ev; route: string | null; data: Record<string, unknown> };

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const NUMERIC_ID_RE = /(?<=\/)\d+(?=\/|$)/g;
const INVOICE_NUMBER_ROUTE_RE = /(?<=^\/billing\/invoices\/)[^/]+(?=\/|$)/;
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_SIZE = 20;
const PING_INTERVAL_MS = 30000;
const PING_CAP = 120;

let sid = "";
let t0 = 0;
let queue: Row[] = [];
let pings = 0;
let warned = false;
let enabled = false;
let initStarted = false;
let left = false;

/** Strips query/hash + token secrets, then collapses uuid/numeric path
 *  segments to ':id' so routes group (e.g. /staff/123 -> /staff/:id). */
export function normalizeRoute(pathname: string): string {
  const path = pathname.split(/[?#]/)[0];
  return redactRoute(path)
    .replace(INVOICE_NUMBER_ROUTE_RE, ':id')
    .replace(UUID_RE, ":id")
    .replace(NUMERIC_ID_RE, ":id");
}

function secs(): number {
  return Math.round((Date.now() - t0) / 1000);
}

function flush(): void {
  if (!enabled || queue.length === 0) return;
  const rows = queue;
  queue = [];
  supabase
    .from("app_events")
    .insert(
      rows.map((r) => ({
        ...r,
        site: "lms" as const,
        ua: navigator.userAgent,
        vw: window.innerWidth,
        vh: window.innerHeight,
      })),
    )
    .then(({ error }) => {
      if (error && !warned) {
        warned = true;
        console.warn("[analytics] insert failed:", error.message);
      }
    });
}

function track(ev: Ev, route: string | null, data: Record<string, unknown> = {}): void {
  if (import.meta.env.DEV) return;
  queue.push({ sid, ev, route, data: { t: secs(), ...data } });
  if (queue.length >= FLUSH_SIZE) flush();
}

function onClick(e: MouseEvent): void {
  const el = (e.target as Element | null)?.closest("a, button, [role=button], summary, select[data-track]");
  if (!el) return;
  let hrefPath: string | undefined;
  const href = el.getAttribute("href");
  if (href) {
    try {
      const url = new URL(href, window.location.origin);
      // Contact buttons must never send a phone number/email address to analytics.
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        hrefPath = normalizeRoute(url.pathname);
      }
    } catch {
      /* not a URL */
    }
  }
  const cls = Array.from(el.classList).slice(0, 2).join(".");
  track("click", normalizeRoute(window.location.pathname), {
    tag: el.tagName.toLowerCase(),
    track: el.getAttribute("data-track") || undefined,
    // Explicit conversion goal (pulse vocabulary) — preferred over button text.
    goal: el.getAttribute("data-goal") || undefined,
    id: el.id || undefined,
    cls: cls || undefined,
    href: hrefPath,
  });
}

function onVisibilityChange(): void {
  const route = normalizeRoute(window.location.pathname);
  if (document.visibilityState === "hidden") {
    if (left) return;
    left = true;
    track("leave", route);
    flush();
  } else if (left) {
    left = false;
    track("return", route);
  }
}

function onPagehide(): void {
  if (left) return;
  left = true;
  track("leave", normalizeRoute(window.location.pathname));
  flush();
}

/** Initializes once; no-op in dev or when there's no signed-in session. */
export async function initAnalytics(): Promise<void> {
  if (initStarted || import.meta.env.DEV) return;
  initStarted = true;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    queue = [];
    return;
  }

  enabled = true;
  t0 = Date.now();
  sid = sessionStorage.getItem("tk_sid") ?? "";
  if (!sid) {
    sid = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    sessionStorage.setItem("tk_sid", sid);
  }

  document.addEventListener("click", onClick, true);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPagehide);
  setInterval(flush, FLUSH_INTERVAL_MS);
  setInterval(() => {
    if (document.visibilityState === "visible" && pings < PING_CAP) {
      pings++;
      track("ping", normalizeRoute(window.location.pathname));
    }
  }, PING_INTERVAL_MS);
}

export function trackView(pathname: string): void {
  track("view", normalizeRoute(pathname));
}

/** A surface's own `view`, carrying a stable node id, on top of the app-wide
 *  route view fired by useAnalytics. Same beacon, same table — never a second
 *  tracking stack (pulse surface law). */
export function trackNodeView(node: string): void {
  track("view", normalizeRoute(window.location.pathname), { node });
}

/** An explicit conversion goal that isn't a DOM click (the `click` listener
 *  above already reports `data-goal` for those) — e.g. a state change driven
 *  by a timer or a finished upload. Same beacon, same table, same `ev`
 *  vocabulary; `data` carries non-personal numbers only (e.g. the
 *  `video_upload` goal's duration_s / size_bytes). */
export function trackGoal(goal: string, data: Record<string, number | string | boolean> = {}): void {
  track("click", normalizeRoute(window.location.pathname), { ...data, goal });
}
